import { cx } from '@emotion/css';
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from '@mui/icons-material';
import { ChangeEvent, cloneElement, FC, InputHTMLAttributes, ReactElement, useEffect, useState } from 'react';

import type { Size } from '../types';

export type CheckboxProps = {
  color?: string;
  // alignLabel?: Alignment; @TODO -> top/right/bottom/left
  id: string;
  name: string;
  checked: boolean;
  disabled?: boolean;
  iconSize?: Size;
  icon?: ReactElement;
  iconChecked?: ReactElement;
  indeterminate?: boolean;
  className?: string;
  dataTestId?: string;
  onChange: (ev: ChangeEvent<HTMLInputElement>) => void;
} & Partial<InputHTMLAttributes<HTMLInputElement>>;

const defaultProps = {
  color: 'primary-500',
  disabled: false,
  iconSize: 'md',
  indeterminate: false,
};

export const Checkbox: FC<CheckboxProps> = (props: CheckboxProps) => {
  const {
    id,
    name,
    checked,
    disabled,
    indeterminate,
    color,
    iconSize,
    icon,
    iconChecked,
    onChange,
    className,
    dataTestId,
    ...restProps
  } = {
    ...defaultProps,
    ...props,
  };
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const checkedClasses = (className?: string) =>
    cx(
      className,
      color && !disabled ? `text-${color}` : 'text-secondary-500',
      disabled ? 'checkbox-disabled cursor-not-allowed' : 'cursor-pointer',
      'checkbox-icon__checked',
    );

  const uncheckedClasses = (className?: string) =>
    cx(
      className,
      'checkbox-icon__unchecked',
      'text-secondary-500',
      disabled ? 'checkbox-disabled cursor-not-allowed' : 'cursor-pointer',
    );

  const checkedIcon = (iconElement: ReactElement) =>
    cloneElement(iconElement, {
      className: checkedClasses(iconElement.props.className),
    });

  const uncheckedIcon = (iconElement: ReactElement) =>
    cloneElement(iconElement, {
      className: uncheckedClasses(iconElement.props.className),
    });

  useEffect(() => {
    if (shouldAnimate) {
      const timeout = setTimeout(() => {
        setShouldAnimate(false);
      }, 800);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [shouldAnimate]);

  return (
    <div
      className={cx(
        className,
        checked ? 'checkbox--selected' : null,
        shouldAnimate ? 'animate-ripple' : null,
        iconSize ? `checkbox-${iconSize}` : null,
        indeterminate ? 'checkbox--indeterminate' : null,
        disabled ? 'checkbox-disabled cursor-not-allowed' : 'cursor-pointer',
        'checkbox flex-none mr-2',
      )}
      {...restProps}>
      <input
        id={id}
        data-testid={dataTestId ? dataTestId : 'checkbox'}
        name={name}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className={cx(disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
        onChange={(ev) => {
          setShouldAnimate(true);
          onChange(ev);
        }}
        ref={(input) => {
          if (input) {
            input.indeterminate = indeterminate as boolean;
          }
        }}
      />
      {iconChecked ? checkedIcon(iconChecked) : <CheckBox className={checkedClasses()} />}
      {icon ? uncheckedIcon(icon) : <CheckBoxOutlineBlank className={uncheckedClasses()} />}
      <IndeterminateCheckBox
        className={cx(
          'checkbox-icon__indeterminate',
          color && !disabled ? `text-${color}` : 'text-secondary-500',
          disabled ? 'checkbox-disabled cursor-not-allowed' : 'cursor-pointer',
        )}
      />
    </div>
  );
};
